import { Box } from '@material-ui/core';
import { Currency } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  AutomobileFormFields,
  AutomobileFormFieldsProps,
} from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/components/AutomobileFormFields';
import { FieldHint } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/components/FieldHint';
import { VehicleAmountField } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/components/VehicleAmountField';
import { AutomobileDataEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileDataEntity';
import { useAutomobileForm } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/hooks/useAutomobileForm';
import { LoadingValues } from '@/components/shared/components/LoadingValues/LoadingValues';
import { Text } from '@/components/shared/components/Text';

interface AutomobileFormProps extends AutomobileFormFieldsProps {
  showLoadingUntilInitialized?: boolean;
  vehicleAmountEditProps?: {
    isVehicleAmountEditable: boolean;
    currency: Currency;
  };
}

export const AutomobileForm = ({
  showLoadingUntilInitialized,
  vehicleAmountEditProps,
  highlightEditableFields = false,
  previousValues,
  dense,
  ...props
}: AutomobileFormProps) => {
  const {
    automobileData,
    createAutomobileDataEntities,
    updateAllData,
    updateSelectedBrand,
    updateSelectedModel,
    updateSelectedVersion,
    isDataInitialized,
  } = useAutomobileForm();
  const { watch, setValue } = useFormContext();

  const selectedBrand = watch('item.brand');
  const selectedModel = watch('item.model');
  const selectedVersion = watch('item.version');

  useEffect(() => {
    if (!isDataInitialized) {
      if (!automobileData) {
        createAutomobileDataEntities(selectedBrand, selectedModel, selectedVersion);
      } else {
        updateAllData();
      }
    }
  }, []);

  useEffect(() => {
    if (automobileData && isDataInitialized && selectedBrand && automobileData.selectedBrand?.value !== selectedBrand) {
      setValue('item.model', '');
      setValue('item.version', '');
      updateSelectedBrand(selectedBrand, automobileData);
    }
  }, [selectedBrand]);

  useEffect(() => {
    if (automobileData && isDataInitialized && selectedModel && automobileData.selectedModel?.value !== selectedModel) {
      setValue('item.version', '');
      updateSelectedModel(selectedModel, automobileData);
    }
  }, [selectedModel]);

  useEffect(() => {
    if (
      automobileData &&
      isDataInitialized &&
      selectedVersion &&
      automobileData.selectedVersion?.value !== selectedVersion
    ) {
      updateVersionData(automobileData);
    }
  }, [selectedVersion]);

  const updateVersionData = async (automobileData: AutomobileDataEntity) => {
    const newAutomobileData = await updateSelectedVersion(selectedVersion, automobileData);
    const dtoData = newAutomobileData?.selectedVersion;
    if (dtoData) {
      setValue('item.brandCode', dtoData.brandCode ?? null);
      setValue('item.modelCode', dtoData.modelCode ?? null);
      setValue('item.versionCode', dtoData.versionCode ?? null);
      setValue('item.autoCategory', dtoData.category ?? null);
      setValue('item.bodyType', dtoData.bodyType ?? null);
      setValue('item.power', dtoData.power ?? null);
      setValue('item.engine', dtoData.engine ?? null);
      setValue('item.gear', dtoData.gear ?? null);
      setValue('item.description', dtoData.description ?? null);
    }
  };

  if (showLoadingUntilInitialized && !isDataInitialized) {
    return (
      <Box
        height={'391px'}
        width={'100%'}
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <LoadingValues titleTextId={'LOADING_AUTO_DETAILS'} />
      </Box>
    );
  }

  return (
    <Box width={'100%'}>
      <AutomobileFormFields
        {...props}
        highlightEditableFields={highlightEditableFields}
        previousValues={previousValues}
        dense={dense}
      />
      {vehicleAmountEditProps?.isVehicleAmountEditable && (
        <Box mt={dense ? 3 : 4}>
          <VehicleAmountField
            currency={vehicleAmountEditProps.currency}
            disabled={props.isFieldDisabled?.('price') ?? false}
            highlightLabel={highlightEditableFields && !(props.isFieldDisabled?.('price') ?? false)}
          >
            {previousValues?.price && (
              <FieldHint>
                <Text tId="PREVIOUS_VALUE" dynamicDataTexts={[previousValues.price]} />
              </FieldHint>
            )}
          </VehicleAmountField>
        </Box>
      )}
    </Box>
  );
};
